import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import styled from "styled-components";
import { Switch, Case } from "react-if";

import UserDataComponent from "./components/user.data.component";
import DeviceComponent from "./components/device.component";
import { useCreateContext } from "./provider/create.provider";
import PrimaryButton from "../../../components/buttons/PrimaryButton";
import { useLoginPolicy } from "../../../services/microservice/policy/auth/use-auth-policy";
import { useLoginUser } from "../../../hooks/useLoginUser";
import { IUseCreatePolicyAgents } from "../../../services/microservice/micelu/create/use-create-micelu.interface";
import { useModalContext } from "../../../providers/modal/modal.provider";
import { useCreatePolicyAgents } from "../../../services/microservice/micelu/create/use-create-micelu";
import ModalHeader from "../../../components/claims/components/ModalHeader";
import ModalBody from "../../../components/claims/components/ModalBody";
import { useVerifyDeviceBySponsor } from "../../../services/microservice/micelu/device/use-device";
import CampaginFinalized from "../../../assets/icons/meeting_out.svg";

interface IInputs {
  name: string;
  typeDocument: string;
  numberDocument: string;
  email: string;
  imei: string;
  deviceVariant: {
    id: string;
    label: string;
  };
  storageCapacity: {
    id: string;
    label: string;
  };
  insuredValue: string;
  plan: number;
  valuePolicie: string;
}

const messageRequired = " Requerido";

const LabelCheckbox = styled.label`
  > input[type="checkbox"]:checked + i {
    background: "#000000" !important;
  }
`;

const splitFullName = (
  fullName: string,
): { firstName: string; lastName: string } => {
  const words = fullName.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return { firstName: "", lastName: "" };
  }

  if (words.length === 1) {
    return { firstName: words[0], lastName: "" };
  }

  if (words.length === 2) {
    return { firstName: words[0], lastName: words[1] };
  }

  if (words.length === 3) {
    return { firstName: words[0], lastName: `${words[1]} ${words[2]}` };
  }

  // 4 o más palabras: 2 nombres, resto apellidos
  return {
    firstName: `${words[0]} ${words[1]}`,
    lastName: words.slice(2).join(" ") || "",
  };
};

const schemaValidation = yup.object().shape({
  name: yup.string().required(messageRequired),
  typeDocument: yup.string().required(messageRequired),
  numberDocument: yup
    .string()
    .matches(/^\d+$/, { message: messageRequired })
    .required(messageRequired),
  email: yup.string().email().required(messageRequired),
  imei: yup
    .string()
    .min(15, { message: messageRequired })
    .max(15, { message: messageRequired })
    .matches(/^\d+$/, { message: messageRequired })
    .required(messageRequired),
  insuredValue: yup.string().required(messageRequired),
  plan: yup.number().required(messageRequired).typeError(messageRequired),
  valuePolicie: yup.string().optional(),
  deviceVariant: yup
    .object({
      id: yup.string().required(messageRequired),
    })
    .required(messageRequired),
  storageCapacity: yup
    .object({
      id: yup.string().required(messageRequired),
    })
    .required(messageRequired),
  confirm: yup.boolean().oneOf([true], messageRequired),
});

const CreateComponent = () => {
  const { referenceTac, infoPolicies } = useCreateContext();
  const { setShow } = useModalContext();
  const { login, logout, isLogin } = useLoginUser();
  const [reloadDevices, setReloadDevices] = useState(false);
  const { mutateAsync: create, isLoading: isCreating } =
    useCreatePolicyAgents();
  const { clientID } = useParams();
  const {
    data: Credentials,
    isLoading,
    refetch,
    isError,
    isSuccess,
  } = useLoginPolicy(clientID);
  const { mutateAsync: verifyDevice } = useVerifyDeviceBySponsor();

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    watch,
    setValue,
    clearErrors,
    reset,
  } = useForm({
    resolver: yupResolver(schemaValidation),
    defaultValues: {
      name: "",
      typeDocument: "CEDULA_CIUDADANIA",
      numberDocument: "",
      email: "",
      imei: "",
      deviceVariant: null,
      storageCapacity: null,
      insuredValue: "",
      plan: undefined,
      valuePolicie: "",
      confirm: false,
    },
  });

  useEffect(() => {
    handleLogin();
  }, [isLoading, reloadDevices, isLogin, isError, isSuccess]);

  const handleLogin = () => {
    if (isError) {
      logout();
    }
    if (!Credentials) return;
    login(Credentials.data.data, false);
  };

  const onSubmit = async (val: IInputs) => {
    const { firstName, lastName } = splitFullName(val.name);
    const data: IUseCreatePolicyAgents = {
      client: {
        firstName,
        lastName,
        genderId: "POR_ACTUALIZAR",
        identification: {
          type: val.typeDocument,
          number: val.numberDocument,
        },
        email: val.email,
        address: "NA",
      },
      planId: infoPolicies[0].id,
      device: {
        line: "0000000000",
        imei: val.imei,
        referenceTac: referenceTac,
      },
      sponsorId: "SAMSUNG",
      priceOptionId: val.plan,
      insuredValue: val.insuredValue,
      comercialName: val.deviceVariant.label
        .replaceAll(/\s*\d+\s*GB/gi, "")
        .trim(),
    };

    try {
      const resVerifyDevice = await verifyDevice({
        imei: val.imei,
        manufacturer: "Samsung",
        model: val.deviceVariant.label
          .replaceAll(/\s*\d+\s*GB/gi, "")
          .trim()
          .replaceAll(" Base", "")
          .replaceAll(" 1TB", ""),
        sponsorID: "SAMSUNG",
      });
      if (resVerifyDevice.data.message === "Ok") {
        const res = await create(data);
        if (res) {
          setShow((prevState) => ({
            ...prevState,
            visible: true,
            header: <ModalHeader success={true} />,
            content: (
              <ModalBody
                title="Creación póliza exitosa"
                message={`La póliza se creo exitosamente con id: ${res.data.policy.id}`}
              />
            ),
            onAccept: clearProcess,
            titleOnAccept: "Aceptar",
            withClass: "flex",
            showBtnAccept: true,
          }));
        }
      }
    } catch (error) {
      if (error.response.status === 400) {
        setShow((prevState) => ({
          ...prevState,
          visible: true,
          header: <ModalHeader success={false} />,
          content: (
            <ModalBody
              title=" No fue posible crear la póliza"
              message={error?.response.data.error.message}
            />
          ),
          onAccept: clearProcess,
          titleOnAccept: "Aceptar",
          withClass: "flex",
          showBtnAccept: true,
        }));
      } else {
        setShow((prevState) => ({
          ...prevState,
          visible: true,
          header: <ModalHeader success={false} />,
          content: (
            <ModalBody
              title=" No fue posible crear la póliza"
              message="Acaba de ocurrir un problema, lo sentimos."
            />
          ),
          onAccept: clearProcess,
          titleOnAccept: "Aceptar",
          withClass: "flex",
          showBtnAccept: true,
        }));
      }
    }
  };

  const clearProcess = async () => {
    reset();
    refetch();
    setShow((prevState) => ({
      ...prevState,
      visible: false,
    }));
    setReloadDevices((prev) => !prev);
  };

  return (
    <div
      className={`flex w-full md:mt-80 md:flex-col lg:mt-32 xl:mt-44 xl:flex-row 2xl:mt-0`}
    >
      <div className="mb-20 w-full rounded-xl bg-white py-5 shadow-xl md:-mt-1 lg:px-10 xl:mt-10">
        <div className="mx-auto w-5/6 overflow-hidden py-10 lg:w-3/4">
          <Switch>
            <Case condition={isLoading}>
              <div className="flex h-96 w-full animate-pulse items-center justify-center rounded-xl bg-gray">
                <p className="text-center text-3xl font-bold"> Cargando ... </p>
              </div>
            </Case>
            <Case condition={isSuccess}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <UserDataComponent control={control} errors={errors} />
                <DeviceComponent
                  control={control}
                  watch={watch}
                  clearErrors={clearErrors}
                  setValue={setValue}
                  errors={errors}
                  reloadDevices={reloadDevices}
                />
                <div className="col-span-2 w-full rounded-xl bg-gray p-3">
                  <LabelCheckbox>
                    <input
                      {...register("confirm", {
                        required: {
                          value: true,
                          message: "* Requerido",
                        },
                      })}
                      id="confirm"
                      name="confirm"
                      type="checkbox"
                      className="mt-5"
                    />
                    <i></i> Confirmo que ya facturé el celular al cliente{" "}
                    <span className="text-3xl text-critical">*</span>
                  </LabelCheckbox>
                  <div className="mb-5 h-1">
                    {errors && (
                      <p className="my-0 py-0 text-base text-critical">
                        {errors.confirm?.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex w-full justify-end">
                  <PrimaryButton
                    title="Asegurar"
                    className={
                      "text-md mt-5 w-36 rounded-3xl bg-textBlack px-5 py-1 font-normal text-whiteSoft"
                    }
                    type="submit"
                    disabled={isCreating}
                    loading={isCreating}
                  />
                </div>
              </form>
            </Case>
            <Case condition={isError}>
              <>
                <h3 className="text-center text-5xl font-bold">
                  Campaña finalizada
                </h3>
                <img
                  src={CampaginFinalized}
                  alt="Campaña finalizada"
                  className="mx-auto my-10"
                />
              </>
            </Case>
          </Switch>
        </div>
      </div>
    </div>
  );
};

export default CreateComponent;
